/**
 * Conversion entre instantes y horas de reloj de pared (ADR-006, ADR-038).
 *
 * **Este es el UNICO archivo del monorepo que puede importar `date-fns-tz`.**
 * Una regla de ESLint lo hace cumplir. El resto del sistema usa las operaciones de
 * negocio de aca y nunca toca la libreria, de modo que cambiarla vuelve a ser un cambio
 * local (ADR-038).
 *
 * ## Por que hay verificacion de ida y vuelta
 *
 * `fromZonedTime` resuelve en silencio las horas que no existen. Comprobado sobre la
 * base IANA de este runtime: pedirle las **02:30 del 2014-10-05** en Montevideo —hora
 * que el pais se salteo al adelantar el reloj— devuelve un instante que corresponde a
 * las **01:30**. Sin verificar, una reserva quedaria una hora antes de lo pedido y nadie
 * se enteraria hasta que el cliente llegara a la cancha.
 *
 * Por eso toda conversion local → instante se verifica volviendo a convertir, y si no
 * coincide se devuelve `HORA_LOCAL_INEXISTENTE`.
 *
 * Uruguay no aplica horario de verano desde el 2015-03-08, asi que hoy estos caminos no
 * se recorren. Estan igual, y probados, porque una regla horaria es una decision
 * administrativa que puede volver a cambiar, y porque el sistema opera 24/7.
 */
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { errorDominio } from '../tipos/errores.js';
import { fallo } from '../tipos/resultado.js';
import { sonIgualesFechaHora, sumarDiasLocales, textoDeFechaLocal, textoDeHoraLocal, } from './civil.js';
import { instanteDesdeEpochMs, sumarMinutos } from './instante.js';
/** Desplazamientos de horario de verano que se prueban al buscar ambiguedad. */
const DESPLAZAMIENTOS_POSIBLES_MINUTOS = [30, 60, 120];
/**
 * Convierte un instante a la hora de reloj de una zona.
 *
 * Es total: todo instante tiene exactamente una hora de reloj en cada zona.
 */
export function localDesdeInstante(instante, zona) {
    const texto = formatInTimeZone(new Date(instante), zona, "yyyy-MM-dd'T'HH:mm");
    const partes = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(texto);
    if (partes === null) {
        // formatInTimeZone con un patron fijo no puede producir otra cosa; si lo hace, la
        // zona no existe y es una violacion de invariante, no un fallo esperable.
        throw new RangeError(`ZONA_INVALIDA: no se pudo interpretar la zona "${zona}".`);
    }
    return {
        fecha: {
            anio: Number(partes[1]),
            mes: Number(partes[2]),
            dia: Number(partes[3]),
        },
        hora: {
            hora: Number(partes[4]),
            minuto: Number(partes[5]),
        },
    };
}
/** Fecha de calendario a la que pertenece un instante en la zona dada. */
export function fechaLocalDesdeInstante(instante, zona) {
    return localDesdeInstante(instante, zona).fecha;
}
/** Hora de reloj de un instante en la zona dada. */
export function horaLocalDesdeInstante(instante, zona) {
    return localDesdeInstante(instante, zona).hora;
}
/** Convierte sin verificar. Uso interno: `instanteDesdeLocal` es la puerta publica. */
function convertirSinVerificar(local, zona) {
    const texto = `${textoDeFechaLocal(local.fecha)} ${textoDeHoraLocal(local.hora)}:00.000`;
    return fromZonedTime(texto, zona).getTime();
}
/**
 * Clasifica una hora de reloj: normal, inexistente o ambigua.
 *
 * Sirve para diagnosticar y para probar. La regla de negocio usa `instanteDesdeLocal`,
 * que ya aplica la politica.
 */
export function clasificarLocal(local, zona) {
    const ms = convertirSinVerificar(local, zona);
    if (Number.isNaN(ms)) {
        throw new RangeError(`ZONA_INVALIDA: no se pudo interpretar la zona "${zona}".`);
    }
    const instante = ms;
    if (!sonIgualesFechaHora(localDesdeInstante(instante, zona), local)) {
        return 'inexistente';
    }
    // Ambigua: existe otro instante, mas adelante, con la misma hora de reloj.
    for (const desplazamiento of DESPLAZAMIENTOS_POSIBLES_MINUTOS) {
        const candidato = sumarMinutos(instante, desplazamiento);
        if (sonIgualesFechaHora(localDesdeInstante(candidato, zona), local)) {
            return 'ambigua';
        }
    }
    return 'normal';
}
/**
 * Convierte una hora de reloj a un instante.
 *
 * - Hora **normal**: la conversion obvia.
 * - Hora **inexistente** (el reloj se adelanto por encima): devuelve
 *   `HORA_LOCAL_INEXISTENTE`. No se elige una hora cercana en silencio.
 * - Hora **ambigua** (el reloj se atraso y la repitio): devuelve la **primera** de las
 *   dos ocurrencias, que es la convencion habitual. Usa `clasificarLocal` si necesitas
 *   saber que ocurrio.
 */
export function instanteDesdeLocal(local, zona, campo) {
    const ms = convertirSinVerificar(local, zona);
    if (Number.isNaN(ms)) {
        return fallo(errorDominio('ZONA_INVALIDA', campo));
    }
    const construido = instanteDesdeEpochMs(ms, campo);
    if (!construido.ok) {
        return construido;
    }
    // La verificacion que impide el error silencioso descrito arriba.
    if (!sonIgualesFechaHora(localDesdeInstante(construido.valor, zona), local)) {
        return fallo(errorDominio('HORA_LOCAL_INEXISTENTE', campo));
    }
    return construido;
}
/** Desfase de la zona respecto de UTC, en minutos, en ese instante. Negativo al oeste. */
export function desfaseMinutos(instante, zona) {
    const local = localDesdeInstante(instante, zona);
    const comoSiFueraUtc = Date.UTC(local.fecha.anio, local.fecha.mes - 1, local.fecha.dia, local.hora.hora, local.hora.minuto);
    // El instante puede tener segundos; se descartan porque el desfase se mide en minutos.
    const instanteSinSegundos = Math.floor(instante / 60_000) * 60_000;
    return (comoSiFueraUtc - instanteSinSegundos) / 60_000;
}
/** Primer instante del dia local. */
export function inicioDelDiaLocal(fecha, zona) {
    return instanteDesdeLocal({ fecha, hora: { hora: 0, minuto: 0 } }, zona, 'fecha');
}
/**
 * Ultimo instante del dia local, con precision de milisegundo.
 *
 * Se calcula como el inicio del dia siguiente menos un milisegundo, y no como
 * "23:59:59.999", porque esa hora podria no existir si alguna vez hubiera un cambio de
 * horario a medianoche.
 */
export function finDelDiaLocal(fecha, zona) {
    // Se usa aritmetica de calendario, no "+24 horas": un dia con cambio de horario dura
    // 23 o 25 horas, y sumar 24 caeria en el dia equivocado.
    const inicioSiguiente = inicioDelDiaLocal(sumarDiasLocales(fecha, 1), zona);
    if (!inicioSiguiente.ok) {
        return inicioSiguiente;
    }
    return instanteDesdeEpochMs(inicioSiguiente.valor - 1, 'fecha');
}
//# sourceMappingURL=zona.js.map